import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import type { HistoryCourseEmployeesFilterI } from 'types';
import useTerminalsApi from './useTerminalsApi';
import useDepartmentsApi from './useDepartmentsApi';
import useJobTitlesApi from './useJobTitlesApi';
import useDebounce from './useDebounce';

interface Props {
  onSearch: (filters: HistoryCourseEmployeesFilterI) => void;
}

const defaultValue: HistoryCourseEmployeesFilterI = {};

const useHistoryCourseEmployeesFilters = ({ onSearch }: Props) => {
  const isFirstRender = useRef(true);

  const { control, reset, getValues, watch } = useForm({
    defaultValues: defaultValue,
  });

  const { data: terminalsData } = useTerminalsApi({ init: true });
  const { data: departmentsData } = useDepartmentsApi({ init: true });
  const { data: jobTitlesData } = useJobTitlesApi({ init: true });

  const handleReset = () => {
    reset(defaultValue);
    onSearch({});
  };

  // Watch for changes in fields
  const searchValue = watch('search');
  const dateRangeValue = watch('dateRange');
  const terminalIdValue = watch('terminalId');
  const departmentIdValue = watch('departmentId');
  const jobTitleIdValue = watch('jobTitleId');
  const finishedDateValue = watch('finishedDate');

  const debouncedSearch = useDebounce(searchValue, 500);

  // Determinar si hay filtros activos
  const hasActiveFilters = !!(
    searchValue ||
    dateRangeValue?.startDate ||
    dateRangeValue?.endDate ||
    terminalIdValue ||
    departmentIdValue ||
    jobTitleIdValue ||
    finishedDateValue
  );

  // Effect to handle debounced search
  useEffect(() => {
    // Evitar ejecución en la primera renderización
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Ejecutar búsqueda cuando el usuario cambia el texto
    if (debouncedSearch !== undefined) {
      const filters = getFiltersForApi();
      onSearch(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const getFiltersForApi = (): HistoryCourseEmployeesFilterI => {
    const values = getValues();
    const filters: HistoryCourseEmployeesFilterI = {};

    if (values.search) filters.search = values.search;
    if (values.dateRange?.startDate) filters.startedAtStart = values.dateRange.startDate.toISOString().split('T')[0];
    if (values.dateRange?.endDate) filters.startedAtEnd = values.dateRange.endDate.toISOString().split('T')[0];
    if (values.terminalId) filters.terminalId = values.terminalId;
    if (values.departmentId) filters.departmentId = values.departmentId;
    if (values.jobTitleId) filters.jobTitleId = values.jobTitleId;
    if (values.finishedDate) {
      const date = values.finishedDate as unknown as { toISODate?: () => string };
      filters.finishedDate = date.toISODate?.() ?? String(values.finishedDate);
    }

    return filters;
  };

  return {
    control,
    isDirty: hasActiveFilters,
    handleReset,
    getFiltersForApi,
    // Data
    terminalsData,
    departmentsData,
    jobTitlesData,
  };
};

export default useHistoryCourseEmployeesFilters;
